/**
 * Local transfer history (guest-first, in localStorage).
 */

import { createRoute } from "@tanstack/solid-router";
import { createSignal, For, Show } from "solid-js";
import { Download, Send as SendIcon, Trash2, Trophy } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { rootRoute } from "@/routes/__root";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { PageBackground } from "@/components/ui/page-background";
import { TopBar } from "@/components/ui/top-bar";
import { WaveIllustration } from "@/components/brand/wave-illustration";
import { clearHistory, type HistoryEntry, loadHistory } from "@/features/transfer/history";
import {
  type AchievementId,
  clearAchievements,
  loadAchievements,
} from "@/features/transfer/achievements";

const ACHIEVEMENT_LABELS: Record<AchievementId, MessageDescriptor> = {
  "first-send": msg`First send`,
  "five-sends": msg`5 transfers`,
  "multi-file": msg`10 files shared`,
  "hundred-mb": msg`100 MB moved`,
  "gigabyte-club": msg`Gigabyte club`,
};

function HistoryPage() {
  const { t } = useI18n();
  const [entries, setEntries] = createSignal<HistoryEntry[]>(loadHistory());
  const [unlocked, setUnlocked] = createSignal<AchievementId[]>(loadAchievements().unlocked);

  const wipe = () => {
    clearHistory();
    clearAchievements();
    setEntries([]);
    setUnlocked([]);
  };

  return (
    <main class="app-screen screen-enter mx-auto w-full max-w-xl gap-7 lg:max-w-3xl">
      <PageBackground />
      <TopBar
        right={
          <Show when={entries().length > 0}>
            <Button
              variant="ghost"
              size="sm"
              onClick={wipe}
              class="gap-1.5"
              data-testid="btn-clear-history"
            >
              <Trash2 class="h-4 w-4" />
              {t(msg`Clear`)}
            </Button>
          </Show>
        }
      />

      <h1 class="font-display text-3xl tracking-tight">{t(msg`History`)}</h1>

      <Show when={unlocked().length > 0}>
        <ul class="flex flex-wrap gap-2" data-testid="achievements-list">
          <For each={unlocked()}>
            {(id) => (
              <li
                class="inline-flex items-center gap-1.5 rounded-full bg-accent px-3.5 py-1.5 text-xs font-semibold text-accent-foreground shadow-sm shadow-accent/25"
                data-testid="achievement-badge"
              >
                <Trophy class="h-3.5 w-3.5" />
                {t(ACHIEVEMENT_LABELS[id])}
              </li>
            )}
          </For>
        </ul>
      </Show>

      <Show
        when={entries().length > 0}
        fallback={
          <div class="flex flex-1 flex-col items-center justify-center gap-4 py-12 text-center">
            <WaveIllustration class="h-28 w-28 text-accent/70" />
            <p class="font-display text-lg text-foreground">{t(msg`No transfers yet`)}</p>
            <p class="max-w-xs text-sm text-muted-foreground">
              {t(msg`Files you send or receive show up here — on this device only.`)}
            </p>
          </div>
        }
      >
        <ul class="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="history-list">
          <For each={entries()}>
            {(entry) => (
              <li class="flex items-center gap-3 rounded-[var(--radius-xl)] border border-border bg-surface/70 p-4 backdrop-blur">
                <span
                  class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                  classList={{
                    "bg-accent/12 text-accent": entry.direction === "sent",
                    "bg-success/15 text-success": entry.direction === "received",
                  }}
                >
                  <Show when={entry.direction === "sent"} fallback={<Download class="h-5 w-5" />}>
                    <SendIcon class="h-5 w-5" />
                  </Show>
                </span>
                <div class="min-w-0 flex-1 text-sm">
                  <p class="truncate font-medium text-foreground">{entry.fileNames.join(", ")}</p>
                  <p class="text-xs text-muted-foreground">{new Date(entry.at).toLocaleString()}</p>
                </div>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </main>
  );
}

export const historyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/history",
  component: HistoryPage,
});
